import { Flex, Table, Text } from "@mantine/core";
import { IconDeviceIpadOff } from "@tabler/icons-react";
import { type CSSProperties, type HTMLAttributes, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useIsOnline } from "src/hooks";
import { StyledTr } from "src/styled";

import Loader from "../Loader/Loader";
import {
  ScrollTableBody,
  ScrollTableHead,
  ScrollTableWrapper,
} from "./ScrollableTable.styled";
import ScrollableTableHeaderCell from "./ScrollableTableHeaderCell";
import ScrollableTableRow, {
  type TRenderScrollableRow,
} from "./ScrollableTableRow";

export interface ITableHeader extends HTMLAttributes<HTMLTableCellElement> {
  name?: string;
  label: string;
  minWidth?: number;
  sortable?: boolean;
}

interface Props<T> {
  data: T[] | undefined;
  headers: ITableHeader[];
  renderRow: TRenderScrollableRow<T>;
  keyExtractor?: (row: T, index: string) => string;
  loading?: boolean;
  style?: CSSProperties;
  sortable?: boolean;
  divider?: boolean;
  red?: boolean;
}

const ScrollableTable = <T extends object>({
  data,
  headers,
  renderRow,
  loading,
  keyExtractor,
  style,
  sortable,
  divider,
  red,
}: Props<T>) => {
  const isOnline = useIsOnline();
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!isOnline) {
      setSearchParams(prev => {
        prev.delete("sort");
        prev.delete("dir");
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  return (
    <ScrollTableWrapper
      type="auto"
      scrollbarSize={12}
      offsetScrollbars
      style={{ marginTop: 12, ...style }}>
      <Table style={{ borderCollapse: "collapse" }}>
        <ScrollTableHead>
          <StyledTr>
            {headers.map(cell => (
              <ScrollableTableHeaderCell
                key={cell.name ?? cell.label}
                style={{ minWidth: cell.minWidth ? cell.minWidth : "initial" }}
                sortable={sortable && isOnline && cell.sortable !== false}
                red={red}
                {...cell}
              />
            ))}
          </StyledTr>
        </ScrollTableHead>

        {!loading && data && data.length > 0 && (
          <ScrollTableBody>
            {data?.map((row, index) => (
              <>
                {divider && index > 0 && <StyledTr style={{ height: 50 }} />}
                <ScrollableTableRow
                  key={keyExtractor ? keyExtractor(row, String(index)) : index}
                  row={row}
                  render={renderRow}
                  index={index}
                />
              </>
            ))}
          </ScrollTableBody>
        )}
      </Table>

      {loading && <Loader />}

      {!loading && (!data || data.length === 0) && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap={8}
          mt={32}>
          <IconDeviceIpadOff size={60} />
          <Text size={20} align="center">
            There is no data to display
          </Text>
        </Flex>
      )}
    </ScrollTableWrapper>
  );
};

export default ScrollableTable;
